import { useAutoneTranslation } from '@autone/translations';
import { PopoverTrigger } from '@radix-ui/react-popover';
import {
  type ForwardedRef,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type RefAttributes,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Badge } from '../../atoms';
import { Skeleton } from '../../atoms';
import {
  TooltipContentCard,
  TooltipProvider,
  TooltipTrigger,
} from '../../atoms';
import { type InputProps } from '../../atoms/inputs/Input';
import { TooltipRoot } from '../../atoms/Tooltip';
import { CancelIcon } from '../../icons';
import { cn } from '../../utils';

import { MultiselectInputBox } from './components/MultiselectInputBox';
import { MultiselectInputDefaultIcons } from './components/MultiselectInputDefaultIcons';
import {
  useMultiselectContext,
  useMultiselectPopoverContext,
} from './MultiselectInput.context';
import {
  type MultiselectBaseProps,
  type MultiselectRenderPropControls,
} from './MultiselectInput.types';

interface MultiselectInputProps<T>
  extends Omit<InputProps, 'id'>,
    MultiselectBaseProps<T> {
  id: string;
  'aria-label'?: string;
  loading?: boolean;
  maxBadges?: number;
  getOptionLabel: (option: T) => string;
  renderIcons?: (controls: MultiselectRenderPropControls<T>) => ReactNode;
  renderInput?: boolean;
}

const defaultRenderIcons = <T,>(controls: MultiselectRenderPropControls<T>) => (
  <MultiselectInputDefaultIcons {...controls} />
);

const MultiselectInputComp = <T,>(
  {
    className,
    id,
    'aria-label': ariaLabel,
    loading,
    options,
    maxBadges,
    getOptionId,
    getOptionLabel,
    renderIcons = defaultRenderIcons,
    renderInput = true,
    placeholder,
    ...props
  }: MultiselectInputProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const { common } = useAutoneTranslation();

  const popoverContext = useMultiselectPopoverContext();
  const multiselectContext = useMultiselectContext();
  const { selected, deselectOptions, disabled } = multiselectContext;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const input = useMemo(() => {
    const renderInputComponent =
      renderInput === true || renderInput === undefined;
    const renderDummyPlaceholder =
      renderInput === false && placeholder && selected.length === 0;

    if (renderInputComponent) {
      return (
        <input
          id={id}
          aria-label={ariaLabel}
          data-testid="multiselect-input"
          placeholder={placeholder ? placeholder : common('select')}
          className={cn(
            'flex outline-none self-stretch min-w-[20%] flex-1 text-sm placeholder:text-muted-foreground disabled:bg-background disabled:opacity-50 min-h-[28px]',
          )}
          ref={inputRef}
          type={'text'}
          disabled={disabled}
          {...props}
        />
      );
    } else if (renderDummyPlaceholder) {
      return <p className="text-muted-foreground">{placeholder}</p>;
    }

    return null;
  }, [
    renderInput,
    placeholder,
    common,
    disabled,
    props,
    selected.length,
    ariaLabel,
    id,
  ]);

  const optionsMap = useMemo(
    () =>
      Object.fromEntries(
        options.map((option) => [getOptionId(option), option]),
      ),
    [options, getOptionId],
  );

  const numSelected = selected.length;
  const renderAllBadges =
    maxBadges === undefined ? true : numSelected <= maxBadges;

  return (
    <PopoverTrigger
      onClick={(e) => (loading || disabled) && e.preventDefault()}
      asChild
    >
      <MultiselectInputBox
        className={cn('group', className)}
        onClick={() => inputRef.current?.focus()}
      >
        {loading ? (
          <Skeleton className="w-full self-stretch" />
        ) : (
          <>
            <div className="w-full flex flex-row flex-wrap gap-2">
              {renderAllBadges ? (
                selected.map((optionId) => (
                  <MultiselectInputSelectedOptionBadge
                    key={optionId}
                    label={
                      optionId in optionsMap
                        ? getOptionLabel(optionsMap[optionId])
                        : ''
                    }
                    onRemoveOption={() => deselectOptions([optionId])}
                  />
                ))
              ) : (
                <TooltipProvider>
                  <TooltipRoot delayDuration={100}>
                    <TooltipTrigger asChild>
                      {/* div required to show tooltip */}
                      <div>
                        <MultiselectInputSelectedOptionBadge
                          label={`${numSelected} ${common('selected')}`}
                          onRemoveOption={() => deselectOptions(selected)}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContentCard className="flex flex-wrap gap-1 p-2 max-w-[400px] max-h-[var(--radix-tooltip-content-available-height)] overflow-y-auto">
                      {selected.map((optionId) => (
                        <MultiselectInputSelectedOptionBadge
                          key={optionId}
                          label={
                            optionId in optionsMap
                              ? getOptionLabel(optionsMap[optionId])
                              : ''
                          }
                          onRemoveOption={() => deselectOptions([optionId])}
                        />
                      ))}
                    </TooltipContentCard>
                  </TooltipRoot>
                </TooltipProvider>
              )}
              {input}
            </div>
            <div className="flex flex-row self-stretch items-center">
              {renderIcons?.({
                ...multiselectContext,
                ...popoverContext,
                options,
                getOptionId,
                disabled,
              })}
            </div>
          </>
        )}
      </MultiselectInputBox>
    </PopoverTrigger>
  );
};

const _MultiselectInput = forwardRef<
  HTMLInputElement,
  MultiselectInputProps<unknown>
>(MultiselectInputComp);

export const MultiselectInput = _MultiselectInput as <T>(
  props: MultiselectInputProps<T> & RefAttributes<HTMLInputElement>,
) => ReactNode;

interface MultiselectInputSelectedOptionBadgeProps {
  label: string;
  onRemoveOption: () => void;
}

const MultiselectInputSelectedOptionBadge = ({
  label,
  onRemoveOption,
}: MultiselectInputSelectedOptionBadgeProps) => {
  const { disabled } = useMultiselectContext();
  const handleRemoveOption = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onRemoveOption();
  };

  return (
    <Badge
      variant="secondary"
      className={cn(
        'flex flex-row items-center gap-1.5',
        disabled && 'text-muted-foreground',
      )}
    >
      {label}
      <div
        data-testid={`badge-remove-button-${label}`}
        aria-disabled={disabled}
        onClick={!disabled ? handleRemoveOption : undefined}
        className="hover:cursor-pointer aria-disabled:cursor-not-allowed"
      >
        <CancelIcon
          size="2xs"
          className={disabled ? 'text-muted-foreground' : undefined}
        />
      </div>
    </Badge>
  );
};
